import React, { useEffect, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import MuiSlider from '@mui/material/Slider';
import { IconButton } from '@mui/material';
import type { SliderProps } from '@mui/material/Slider/Slider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';

type HeartSliderProps = Omit<SliderProps, 'value'> & {
    name?: string;
    link?: string;
    value?: number;
    highlighted?: boolean;
    settings?: React.ReactNode;
    disabled?: boolean;
    revealed?: boolean;
    onNameClick?: () => void;
    onSettingsClick?: () => void;
};

let sharedAudioCtx: AudioContext | null = null;
let sharedNoiseBuffer: AudioBuffer | null = null;
const lastDragSoundAt: Record<string, number> = {};

function getAudioContext(): AudioContext | null {
    try {
        if (!sharedAudioCtx) {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;

            if (!AudioContextClass) {
                return null;
            }

            sharedAudioCtx = new AudioContextClass();
        }

        if (sharedAudioCtx.state === 'suspended') {
            sharedAudioCtx.resume().catch(() => {
            });
        }

        return sharedAudioCtx;
    } catch (e) {
        return null;
    }
}

function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (sharedNoiseBuffer && sharedNoiseBuffer.sampleRate === ctx.sampleRate) {
        return sharedNoiseBuffer;
    }

    const length = Math.floor(ctx.sampleRate * 1.5);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;

    for (let i = 0; i < length; i++) {
        last = 0.82 * last + 0.18 * (Math.random() * 2 - 1);
        data[i] = last * 2.2;
    }

    sharedNoiseBuffer = buffer;

    return buffer;
}

function airFrequency(value: number): number {
    const clamped = Math.max(0, Math.min(100, value));

    return 280 * Math.pow(10, clamped / 100);
}

function playWhoosh(ctx: AudioContext, f0: number, f1: number, at: number, duration: number, volume: number) {
    const source = ctx.createBufferSource();
    const bandpass = ctx.createBiquadFilter();
    const lowpass = ctx.createBiquadFilter();
    const gain = ctx.createGain();

    source.buffer = getNoiseBuffer(ctx);
    source.loop = true;

    bandpass.type = 'bandpass';
    bandpass.Q.value = 0.8;
    bandpass.frequency.setValueAtTime(f0, at);
    bandpass.frequency.exponentialRampToValueAtTime(f1, at + duration);

    lowpass.type = 'lowpass';
    lowpass.frequency.value = 4200;

    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(volume, at + duration * 0.45);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    source.connect(bandpass);
    bandpass.connect(lowpass);
    lowpass.connect(gain);
    gain.connect(ctx.destination);

    source.start(at, Math.random());
    source.stop(at + duration + 0.05);
}

export function playDragSound(key: string, from: number, to: number) {
    if (to === from) {
        return;
    }

    const now = Date.now();

    if (lastDragSoundAt[key] && now - lastDragSoundAt[key] < 140) {
        return;
    }

    lastDragSoundAt[key] = now;

    const ctx = getAudioContext();

    if (!ctx) {
        return;
    }

    const rising = to > from;
    const edge = rising ? Math.max(0, to - 15) : Math.min(100, to + 15);

    playWhoosh(ctx, airFrequency(edge), airFrequency(to), ctx.currentTime + 0.005, 0.22, 0.09);
}

const HEART_PATH = 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';
const HEART_MIN_SIZE = 16;
const HEART_MAX_SIZE = 26;
const HEART_FILL_TOP = 2.5;
const HEART_FILL_BOTTOM = 21.5;

const HeartLevelIcon = ({ value, accentColor, clipId }: { value: number; accentColor: string; clipId: string }) => {
    const fillHeight = ((HEART_FILL_BOTTOM - HEART_FILL_TOP) * value) / 100;
    const fillY = HEART_FILL_BOTTOM - fillHeight;
    const size = HEART_MIN_SIZE + (value / 100) * (HEART_MAX_SIZE - HEART_MIN_SIZE);

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            style={{ transition: 'width 200ms ease-out, height 200ms ease-out' }}
        >
            <defs>
                <clipPath id={clipId}>
                    <path d={HEART_PATH}/>
                </clipPath>
            </defs>
            <path d={HEART_PATH} fill="#ffffff"/>
            <path d={HEART_PATH} fill="none" stroke={accentColor} strokeWidth={1.5}/>
            <rect
                x={0}
                y={fillY}
                width={24}
                height={fillHeight}
                fill={accentColor}
                clipPath={`url(#${clipId})`}
                style={{ transition: 'y 200ms ease-out, height 200ms ease-out' }}
            />
        </svg>
    );
};

const HeartSlider = ({
    name = '',
    link,
    value = 0,
    highlighted = false,
    settings,
    disabled = false,
    revealed = true,
    onNameClick,
    onSettingsClick,
    onChange,
    ...props
}: HeartSliderProps) => {
    const [ isSettingsOpen, setIsSettingsOpen ] = useState(false);
    const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
    const accentColor = 'var(--app-primary, #2549c7)';
    const instanceId = useId().replace(/:/g, '');
    const clipId = `heart-slider-fill-${instanceId}`;
    const lastValueRef = useRef(safeValue);

    useEffect(() => {
        if (lastValueRef.current !== safeValue) {
            playDragSound(instanceId, lastValueRef.current, safeValue);
        }

        lastValueRef.current = safeValue;
    }, [ safeValue, instanceId ]);

    const handleSliderChange: NonNullable<SliderProps['onChange']> = (event, newValue, activeThumb) => {
        const numericValue = Array.isArray(newValue) ? newValue[0] : newValue;

        playDragSound(instanceId, lastValueRef.current, numericValue);
        lastValueRef.current = numericValue;
        onChange?.(event, newValue, activeThumb);
    };

    const handleNameClick = () => {
        onNameClick?.();

        if (link) {
            window.open(link, '_blank');
        }

        if (settings) {
            setIsSettingsOpen(prev => !prev);
        }
    };

    const handleCardClick = () => {
        if (!revealed) {
            onNameClick?.();
        }
    };

    return (
        <Box
            onClick={handleCardClick}
            sx={{
                background: highlighted ? 'rgba(37, 73, 199, 0.08)' : '#ffffff',
                border: '1.5px solid',
                borderColor: highlighted ? accentColor : '#e2e8f0',
                borderRadius: '12px',
                padding: '8px 12px',
                cursor: revealed ? 'default' : 'pointer',
                transition: 'background-color 200ms ease-out, border-color 200ms ease-out',
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', minHeight: 40 }}>
                <Box
                    aria-hidden="true"
                    sx={{
                        flexGrow: revealed ? 0 : 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        minWidth: 0,
                        transition: 'flex-grow 550ms cubic-bezier(.2,.8,.2,1)',
                    }}
                />

                <Box
                    component="button"
                    type="button"
                    onClick={handleNameClick}
                    sx={{
                        position: 'relative',
                        flex: '0 0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        height: 36,
                        padding: revealed ? '0 10px 0 0' : '0',
                        border: 0,
                        background: 'transparent',
                        cursor: !revealed || link ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                        fontSize: revealed ? 15 : 14,
                        fontWeight: 800,
                        color: highlighted ? accentColor : '#1e2f46',
                        letterSpacing: '-0.01em',
                        transition: 'font-size 450ms ease-out, padding 450ms ease-out, color 450ms ease-out',

                        '&:hover': {
                            textDecoration: link ? 'underline' : 'none',
                        },
                    }}
                >
                    {name}

                    {settings && revealed && (
                        <KeyboardArrowDownIcon
                            sx={{
                                fontSize: 16,
                                color: highlighted ? accentColor : '#64748b',
                                transform: isSettingsOpen ? 'rotate(180deg)' : 'none',
                                transition: 'transform 200ms ease-out',
                            }}
                        />
                    )}
                </Box>

                {revealed && onSettingsClick && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSettingsClick();
                        }}
                        aria-label={`${name} settings`}
                        title={`${name} settings`}
                        sx={{ flex: '0 0 auto', p: 0.5, ml: '2px' }}
                    >
                        <SettingsIcon
                            sx={{
                                fontSize: 17,
                                color: highlighted ? accentColor : '#64748b',
                            }}
                        />
                    </IconButton>
                )}

                <Box
                    aria-hidden="true"
                    sx={{
                        flexGrow: revealed ? 0 : 1,
                        flexShrink: 1,
                        flexBasis: 0,
                        minWidth: 0,
                        transition: 'flex-grow 550ms cubic-bezier(.2,.8,.2,1)',
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        flex: revealed ? '0 0 70%' : '0 0 0px',
                        width: revealed ? '70%' : 0,
                        marginLeft: revealed ? 'auto' : 0,
                        height: HEART_MAX_SIZE,
                        opacity: revealed ? 1 : 0,
                        overflow: revealed ? 'visible' : 'hidden',
                        pointerEvents: revealed ? 'auto' : 'none',
                        transition: 'flex-basis 700ms cubic-bezier(.2,.8,.2,1), width 700ms cubic-bezier(.2,.8,.2,1), opacity 500ms ease-out',
                    }}
                >
                    <MuiSlider
                        aria-label={name}
                        value={safeValue}
                        disabled={disabled}
                        onChange={handleSliderChange}
                        {...props}
                        sx={{
                            height: HEART_MAX_SIZE,
                            width: `calc(100% - ${HEART_MAX_SIZE}px)`,
                            mx: `${HEART_MAX_SIZE / 2}px`,
                            padding: '0 !important',

                            '& .MuiSlider-rail': {
                                height: 6,
                                top: '50%',
                                borderRadius: 999,
                                opacity: 1,
                                background: `linear-gradient(
                                    90deg,
                                    #c7d2fe 0%,
                                    ${accentColor} ${safeValue}%,
                                    #e2e8f0 ${safeValue}%,
                                    #e2e8f0 100%
                                )`,
                            },

                            '& .MuiSlider-track': {
                                display: 'none',
                            },

                            '& .MuiSlider-thumb': {
                                width: HEART_MAX_SIZE,
                                height: HEART_MAX_SIZE,
                                backgroundColor: 'transparent',
                                boxShadow: 'none',

                                '&:before': {
                                    display: 'none',
                                },

                                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                                    boxShadow: 'none',
                                },
                            },

                            '&.Mui-disabled': {
                                opacity: 0.55,
                            },
                        }}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: `calc(${HEART_MAX_SIZE / 2}px + (100% - ${HEART_MAX_SIZE}px) * ${safeValue} / 100)`,
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            pointerEvents: 'none',
                            filter: 'drop-shadow(0 2px 4px rgba(15, 23, 42, 0.25))',
                            transition: 'left 400ms ease-out',
                        }}
                    >
                        <HeartLevelIcon value={safeValue} accentColor={accentColor} clipId={clipId}/>
                    </Box>
                </Box>
            </Box>

            {settings && isSettingsOpen && revealed && (
                <Box sx={{ mt: 1.5 }}>
                    {settings}
                </Box>
            )}
        </Box>
    );
};

export default HeartSlider;
